import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

type SectionPayload = {
  posit_id: string;
  section_id?: string;
  parent_id?: string | null;
  title: string;
  content_md: string;
  is_public?: boolean;
  order_index?: number;
  tags?: string[];
  slug?: string;
  anchor_id?: string;
  update_lead?: boolean;
};

export const useWikiMutations = (positId: string | undefined) => {
  const qc = useQueryClient();

  const upsertSection = useMutation({
    mutationFn: async (payload: SectionPayload) => {
      const { data, error } = await supabase.functions.invoke('update-wiki-section', { body: payload });
      if (error || !data?.success) throw new Error(error?.message || data?.error || 'Update failed');
      return data.data;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['wiki-page', positId] });
    }
  });

  const deleteSection = useMutation({
    mutationFn: async (sectionId: string) => {
      const { data, error } = await supabase.functions.invoke('delete-wiki-section', { body: { posit_id: positId, section_id: sectionId } });
      if (error || !data?.success) throw new Error(error?.message || data?.error || 'Delete failed');
      return true;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['wiki-page', positId] });
    }
  });

  const reorderSections = useMutation({
    mutationFn: async (orders: { section_id: string; order_index: number }[]) => {
      const { data, error } = await supabase.functions.invoke('reorder-wiki-sections', { body: { posit_id: positId, orders } });
      if (error || !data?.success) throw new Error(error?.message || data?.error || 'Reorder failed');
      return true;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['wiki-page', positId] });
    }
  });

  return { upsertSection, deleteSection, reorderSections };
};


